// Notepad (2026 redesign). One responsive markup, JSON endpoints, autosave.
// All user data is rendered via textContent / createElement — never string HTML —
// except the note body, which the server sanitizes (NoteHtml) before storage.
import { Note } from "./CRUDs/entities/Note";

const CSRF = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

const api = {
    async call(method, url, body) {
        const res = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': CSRF(),
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: body === undefined ? undefined : JSON.stringify(body),
        });
        if (!res.ok) {
            const payload = await res.json().catch(() => ({}));
            throw Object.assign(new Error(payload.message || `Request failed (${res.status})`), { status: res.status, payload });
        }
        return res.json();
    },
    get: (url) => api.call('GET', url),
    post: (url, body) => api.call('POST', url, body),
    patch: (url, body) => api.call('PATCH', url, body),
    delete: (url) => api.call('DELETE', url),
};

const state = {
    folders: [],
    notes: [],
    caps: {},
    filter: { type: 'all', folderId: null },
    search: '',
    sort: 'updated',
    openId: null,
    detail: null,
    dirty: false,
    saveTimer: null,
    saving: false,
    page: 1,
    hasMore: false,
    total: 0,
    counts: { all: 0, pinned: 0, shared: 0 },
    loading: false,
};

const $id = (id) => document.getElementById(id);

function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
}

/* ---------------------------------------------------------------- list */

// The server filters, searches, sorts and pages. Anything patched in locally
// (a pin toggle, a rename) is re-sorted here so it lands in the right place
// without a refetch.
function visibleNotes() {
    const notes = [...state.notes];
    const bySort = {
        updated: (a, b) => (b.updated_ts || 0) - (a.updated_ts || 0),
        name: (a, b) => a.name.localeCompare(b.name),
        created: (a, b) => b.id - a.id,
    }[state.sort] || (() => 0);
    notes.sort(bySort);
    notes.sort((a, b) => Number(b.pinned) - Number(a.pinned));
    return notes;
}

function renderRail() {
    $id('bb-notepad').querySelectorAll('.bbnp-rail__item[data-filter]').forEach(item => {
        item.classList.toggle('is-on', state.filter.type === item.dataset.filter);
    });
    const counts = state.counts || { all: 0, pinned: 0, shared: 0 };
    for (const key of ['all', 'pinned', 'shared']) {
        const slot = document.querySelector(`[data-count="${key}"]`);
        // always render the number — a blank slot reads as unfinished, not as "none"
        if (slot) slot.textContent = String(counts[key] ?? 0);
    }

    const wrap = $id('bbnp-folders');
    wrap.textContent = '';
    for (const folder of state.folders) {
        const row = el('div', 'bbnp-rail__item bbnp-rail__item--folder');
        row.dataset.folderId = folder.id;
        if (state.filter.type === 'folder' && state.filter.folderId === folder.id) row.classList.add('is-on');

        const ico = el('span', 'bbnp-rail__ico bbnp-folder-ico');
        ico.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>';
        row.appendChild(ico);
        row.appendChild(el('span', 'bbnp-rail__name', folder.name));
        row.appendChild(el('span', 'bbnp-rail__count', String(folder.count ?? 0)));

        if (state.caps.full_access) {
            const edit = el('button', 'bbnp-rail__edit');
            edit.type = 'button';
            edit.title = 'Rename or delete';
            edit.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>';
            edit.addEventListener('click', (ev) => { ev.stopPropagation(); openFolderModal(folder); });
            row.appendChild(edit);
        }

        row.addEventListener('click', () => {
            state.filter = { type: 'folder', folderId: folder.id };
            renderRail();
            fetchList();
        });
        row.addEventListener('dragover', (ev) => { ev.preventDefault(); row.classList.add('is-drop'); });
        row.addEventListener('dragleave', () => row.classList.remove('is-drop'));
        row.addEventListener('drop', async (ev) => {
            ev.preventDefault();
            row.classList.remove('is-drop');
            const noteId = Number(ev.dataTransfer.getData('text/bbnp-note'));
            if (noteId) await moveNote(noteId, folder.id);
        });
        wrap.appendChild(row);
    }
}

function renderCards() {
    const wrap = $id('bbnp-cards');
    wrap.textContent = '';
    const notes = visibleNotes();
    const total = state.total || notes.length;
    $id('bbnp-count').textContent = total === 1 ? '1 note' : `${total} notes`;

    if (!notes.length) {
        const empty = el('div', 'bbnp-cards__empty');
        empty.appendChild(el('div', 'bbnp-cards__empty-title', state.search ? 'Nothing found' : 'No notes here yet'));
        empty.appendChild(el('div', 'bbnp-cards__empty-sub', state.search ? 'Try a different search.' : 'Create your first note with “New note”.'));
        wrap.appendChild(empty);
        return;
    }

    for (const note of notes) {
        const card = el('article', 'bbnp-card');
        card.dataset.id = note.id;
        if (note.id === state.openId) card.classList.add('is-on');
        card.draggable = note.can_edit;
        card.addEventListener('dragstart', (ev) => ev.dataTransfer.setData('text/bbnp-note', String(note.id)));

        const title = el('div', 'bbnp-card__title');
        if (note.pinned) {
            const pin = el('span', 'bbnp-card__pin');
            pin.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 17l-5.9 3.1 1.1-6.6L2.5 8.9l6.6-1L12 2l2.9 5.9 6.6 1-4.7 4.6 1.1 6.6z"/></svg>';
            title.appendChild(pin);
        }
        title.appendChild(el('span', '', note.name));
        card.appendChild(title);
        if (note.preview) card.appendChild(el('div', 'bbnp-card__preview', note.preview));

        const meta = el('div', 'bbnp-card__meta');
        if (state.filter.type !== 'folder' && note.folder_name) meta.appendChild(el('span', 'bbnp-card__chip', note.folder_name));
        if (note.updated_human) meta.appendChild(el('span', '', note.updated_human));
        if (note.attachments) {
            const att = el('span', 'bbnp-card__att');
            att.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" width="12" height="12"><path d="M21 12.5 12.7 20.8a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7L10.4 18.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8"/></svg>';
            att.appendChild(document.createTextNode(String(note.attachments)));
            meta.appendChild(att);
        }
        if (note.shared > 0) {
            const sh = el('span', 'bbnp-card__att');
            sh.title = note.shared === 1 ? 'Shared with 1 person' : `Shared with ${note.shared} people`;
            sh.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" width="12" height="12"><circle cx="9" cy="8" r="3"/><path d="M3 19c0-3 2.7-5 6-5s6 2 6 5"/><path d="M16 6.5a3 3 0 0 1 0 5.5M18 19c0-2-.7-3.5-2-4.5"/></svg>';
            sh.appendChild(document.createTextNode(String(note.shared)));
            meta.appendChild(sh);
        }
        if (!note.can_edit) {
            const lock = el('span', 'bbnp-card__lock');
            lock.title = 'View only';
            lock.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" width="12" height="12"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';
            meta.appendChild(lock);
        }
        card.appendChild(meta);

        card.addEventListener('click', () => openNote(note.id));
        wrap.appendChild(card);
    }
}

/* ---------------------------------------------------------------- editor */

let noteEntity = null;

async function openNote(id) {
    await flushSave();
    try {
        const detail = await api.get(`/notes/note/${id}`);
        state.openId = id;
        state.detail = detail;
        renderCards();
        renderEditor();
        document.getElementById('bb-notepad').classList.add('is-doc-open'); // mobile push
    } catch (e) {
        setSavedStatus('idle');
    }
}

function renderEditor() {
    const d = state.detail;
    $id('bbnp-empty').classList.add('d-none');
    $id('bbnp-doc').classList.remove('d-none');

    const title = $id('bbnp-title');
    title.textContent = d.name;
    title.contentEditable = d.can_edit ? 'true' : 'false';

    const body = $id('bbnp-body');
    body.innerHTML = d.body || '';
    body.contentEditable = d.can_edit ? 'true' : 'false';

    const crumb = $id('bbnp-crumb');
    crumb.textContent = '';
    for (const folder of state.folders) {
        const opt = el('option', '', folder.name);
        opt.value = folder.id;
        if (folder.id === d.folder_id) opt.selected = true;
        crumb.appendChild(opt);
    }
    crumb.disabled = !d.can_edit;

    renderMeta();

    $id('bbnp-toolbar').classList.toggle('d-none', !d.can_edit);
    $id('bbnp-pin').classList.toggle('is-on', d.pinned);
    $id('bbnp-pin').classList.toggle('d-none', !d.can_edit);
    // Delete is the only entry, so hide the whole menu for read-only viewers
    $id('bbnp-more-btn').classList.toggle('d-none', !d.can_edit);
    $id('bbnp-more-menu').classList.add('d-none');
    $id('bbnp-attach').classList.toggle('d-none', !d.can_edit);
    $id('bbnp-share-btn').classList.toggle('d-none', !d.share);
    renderSharedWith();
    $id('bbnp-attach').dataset.noteId = d.id;
    $id('bbnp-attach').dataset.id = d.id;
    $id('bbnp-attach').dataset.entity = 'note';

    renderAttachments();
    renderLinks();
    setSavedStatus('idle');

    noteEntity = new Note(d.id);
    noteEntity.SuccessCallback = () => refreshDetailQuiet();
    noteEntity.scopeOpenDropZoneListener({}, () => refreshDetailQuiet());
    noteEntity.setAttachmentCallbackDelete(() => refreshDetailQuiet());
}

const ICON_DOC = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg>';
const ICON_IMG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="m5 18 5-5 3 3 3-3 3 3"/></svg>';

function renderAttachments() {
    const d = state.detail;
    const wrap = $id('bbnp-docs');
    wrap.textContent = '';
    for (const file of d.attachments || []) {
        const chip = el('div', 'bbnp-file');
        const ico = el('span', 'bbnp-file__ico');
        ico.innerHTML = file.is_image ? ICON_IMG : ICON_DOC;
        chip.appendChild(ico);

        const info = el('span', 'bbnp-file__info');
        info.appendChild(el('span', 'bbnp-file__name', file.name));
        if (file.size) info.appendChild(el('span', 'bbnp-file__size', file.size));
        chip.appendChild(info);

        const actions = el('span', 'bbnp-file__actions');
        if (file.is_image) {
            const eye = el('button', 'bbnp-file__act');
            eye.type = 'button'; eye.title = 'Preview';
            eye.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>';
            eye.addEventListener('click', () => {
                const modal = document.getElementById('preview-modal');
                if (modal) {
                    modal.querySelector('#preview-image').setAttribute('src', file.url);
                    window.$('#preview-modal').modal('show');
                } else {
                    window.open(file.url, '_blank', 'noopener');
                }
            });
            actions.appendChild(eye);
        }
        const dl = el('a', 'bbnp-file__act');
        dl.href = file.url; dl.download = file.name; dl.title = 'Download';
        dl.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16"/></svg>';
        actions.appendChild(dl);

        if (d.can_edit) {
            const del = el('button', 'bbnp-file__act bbnp-file__act--danger');
            del.type = 'button'; del.title = 'Remove file';
            del.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13"/></svg>';
            del.addEventListener('click', () => confirmRemoveFile(file));
            actions.appendChild(del);
        }
        chip.appendChild(actions);
        wrap.appendChild(chip);
    }
}

function confirmRemoveFile(file) {
    $id('bbnp-file-delete-name').textContent = file.name;
    const btn = $id('bbnp-file-delete-confirm');
    btn.onclick = async () => {
        const lc = window.BBButtonLifecycle;
        if (lc) lc.start(btn, { label: 'Removing...' });
        try {
            const body = new FormData();
            body.append('fileId', file.id);
            body.append('_token', CSRF());
            const res = await fetch(`/notes/note/${state.openId}/delete-attachment`, {
                method: 'POST', body, headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (!res.ok) throw new Error('failed');
            if (lc) lc.success(btn, { label: 'Removed', message: 'File removed.' });
            state.detail.attachments = (state.detail.attachments || []).filter(f => f.id !== file.id);
            renderAttachments();
            await refreshData(false);
            setTimeout(() => window.$('#bbnp-file-delete-modal').modal('hide'), 700);
        } catch (e) {
            if (lc) lc.error(btn, { label: 'Try again', message: 'Could not remove the file.' });
        }
    };
    window.$('#bbnp-file-delete-modal').modal('show');
}

// Shared state was only visible inside the Share dialog — nothing on the note
// itself said who could see it. Show the people, with roles in the tooltip.
function renderSharedWith() {
    const slot = $id('bbnp-shared-with');
    const people = state.detail?.shared_with || [];
    slot.textContent = '';
    slot.classList.toggle('d-none', people.length === 0);
    if (!people.length) return;

    for (const person of people.slice(0, 3)) {
        const initials = person.name.split(' ').map(p => p[0] || '').join('').slice(0, 2).toUpperCase();
        const dot = el('span', 'bbnp-avatar' + (person.role === 'editor' ? ' is-editor' : ''), initials);
        dot.title = `${person.name} — can ${person.role === 'editor' ? 'edit' : 'view'}`;
        slot.appendChild(dot);
    }
    if (people.length > 3) slot.appendChild(el('span', 'bbnp-avatar bbnp-avatar--more', '+' + (people.length - 3)));

    slot.title = people.map(p => `${p.name} (can ${p.role === 'editor' ? 'edit' : 'view'})`).join('\n');
}

function renderMeta() {
    const d = state.detail;
    if (!d) return;
    const bits = [];
    if (d.created_human) bits.push(`Created ${d.created_human}${d.author ? ' by ' + d.author : ''}`);
    if (d.updated_human) bits.push(`Edited ${d.updated_human}${d.editor ? ' by ' + d.editor : ''}`);
    $id('bbnp-meta').textContent = bits.join(' · ');
}

function renderLinks() {
    const d = state.detail;
    const wrap = $id('bbnp-links');
    wrap.textContent = '';
    for (const link of d.links || []) {
        const chip = el('span', 'bbnp-link-chip');
        chip.appendChild(el('span', '', link.label));
        if (d.can_edit) {
            const x = el('button', 'bbnp-link-chip__x', '×');
            x.type = 'button';
            x.title = 'Remove link';
            x.addEventListener('click', async () => {
                const detail = await api.delete(`/notes/note/${d.id}/links/${link.id}`);
                state.detail = detail;
                renderLinks();
            });
            chip.appendChild(x);
        }
        wrap.appendChild(chip);
    }
    $id('bbnp-link-add').classList.toggle('d-none', !d.can_edit);
    // the "Linked to:" label only earns its place once something is linked
    const hasLinks = (d.links || []).length > 0;
    $id('bbnp-links-row').classList.toggle('d-none', !d.can_edit && !hasLinks);
    $id('bbnp-links-row').querySelector('.bbnp-links__label').classList.toggle('d-none', !hasLinks);
}

async function refreshDetailQuiet() {
    if (!state.openId) return;
    try {
        state.detail = await api.get(`/notes/note/${state.openId}`);
        renderAttachments();
        renderLinks();
        await refreshData(false);
    } catch (e) { /* transient */ }
}

/* ---------------------------------------------------------------- saving */

function setSavedStatus(mode, message) {
    const slot = $id('bbnp-saved');
    slot.className = 'bbnp-saved';
    slot.title = '';
    if (mode === 'saving') { slot.textContent = 'Saving…'; slot.classList.add('is-saving'); }
    else if (mode === 'saved') { slot.textContent = 'All changes saved'; slot.classList.add('is-saved'); }
    else if (mode === 'error') {
        slot.textContent = message || 'Couldn’t save — will retry';
        slot.title = message || '';
        slot.classList.add('is-error');
    }
    else slot.textContent = '';
}

function queueSave() {
    if (!state.detail?.can_edit) return;
    state.dirty = true;
    setSavedStatus('saving');
    clearTimeout(state.saveTimer);
    state.saveTimer = setTimeout(saveNow, 1200);
}

async function saveNow() {
    if (!state.dirty || !state.openId || state.saving) return;
    state.saving = true;
    const id = state.openId;
    const name = $id('bbnp-title').textContent.trim() || 'Untitled note';
    const body = $id('bbnp-body').innerHTML;
    try {
        const res = await api.patch(`/notes/note/${id}`, { name, body_html: body });
        state.dirty = false;
        setSavedStatus('saved');
        const item = state.notes.find(n => n.id === id);
        if (item) {
            item.name = name;
            item.preview = $id('bbnp-body').textContent.slice(0, 160);
            item.updated_human = 'now';
            item.updated_ts = Math.floor(Date.now() / 1000);
        }
        // keep the open note's own "Edited …" line in step with the save
        if (state.detail) {
            state.detail.updated_human = res.updated_human || state.detail.updated_human;
            state.detail.editor = res.editor || state.detail.editor;
            renderMeta();
        }
        renderCards();
    } catch (e) {
        // 422 = the server refused this content (e.g. note too big). Retrying
        // forever would just hammer it, so surface the reason and stop.
        if (e.status === 422) {
            state.dirty = false;
            const msg = e.payload?.errors?.body_html?.[0]
                || e.payload?.message
                || 'This note could not be saved.';
            setSavedStatus('error', msg);
            clearTimeout(state.saveTimer);
        } else {
            setSavedStatus('error');
            clearTimeout(state.saveTimer);
            state.saveTimer = setTimeout(saveNow, 5000);
        }
    } finally {
        state.saving = false;
    }
}

async function flushSave() {
    clearTimeout(state.saveTimer);
    if (state.dirty) await saveNow();
}

/* ---------------------------------------------------------------- data */

function applyPayload(payload, append = false) {
    state.folders = payload.folders || [];
    state.notes = append ? state.notes.concat(payload.notes || []) : (payload.notes || []);
    if (payload.counts) state.counts = payload.counts;
    if (typeof payload.total === 'number') state.total = payload.total;
    state.hasMore = !!payload.has_more;
    state.page = payload.page || 1;
    if (payload.capabilities) state.caps = payload.capabilities;
    if (state.filter.type === 'folder' && !state.folders.some(f => f.id === state.filter.folderId)) {
        state.filter = { type: 'all', folderId: null };
    }
    renderRail();
    renderCards();
}

/* Mutations return only what changed — patch it in rather than re-downloading
   the whole list (which was ~95 KB at 300 notes, per pin toggle). */
function applyChange(res) {
    if (res.counts) state.counts = res.counts;
    if (Array.isArray(res.folders)) {
        state.folders = res.folders;
        if (state.filter.type === 'folder' && !state.folders.some(f => f.id === state.filter.folderId)) {
            state.filter = { type: 'all', folderId: null };
        }
    }
    if (res.note) {
        const i = state.notes.findIndex(n => n.id === res.note.id);
        if (i === -1) state.notes.unshift(res.note);
        else state.notes[i] = res.note;
    }
    if (res.removed_id) {
        state.notes = state.notes.filter(n => n.id !== res.removed_id);
    }
    renderRail();
    renderCards();
}

function listQuery(page) {
    const p = new URLSearchParams({ page: String(page), filter: state.filter.type, sort: state.sort });
    if (state.filter.type === 'folder' && state.filter.folderId) p.set('folder_id', String(state.filter.folderId));
    if (state.search) p.set('q', state.search);
    return `/notes/data?${p.toString()}`;
}

async function fetchList({ append = false } = {}) {
    if (state.loading) return;
    state.loading = true;
    try {
        const page = append ? state.page + 1 : 1;
        const payload = await api.get(listQuery(page));
        applyPayload(payload, append);
    } finally {
        state.loading = false;
    }
}

async function refreshData(closeIfGone = true) {
    await fetchList();
    if (closeIfGone && state.openId && !state.notes.some(n => n.id === state.openId)) {
        closeEditor();
    }
}

function closeEditor() {
    state.openId = null;
    state.detail = null;
    $id('bbnp-doc').classList.add('d-none');
    $id('bbnp-empty').classList.remove('d-none');
    document.getElementById('bb-notepad').classList.remove('is-doc-open');
    renderCards();
}

async function moveNote(noteId, folderId) {
    const payload = await api.post(`/notes/note/${noteId}/move`, { folder_id: folderId });
    applyChange(payload);
    if (state.openId === noteId && state.detail) {
        state.detail.folder_id = folderId;
        $id('bbnp-crumb').value = String(folderId);
    }
}

/* ---------------------------------------------------------------- modals */

function lifecycle(btn, promise, { success, error }) {
    const lc = window.BBButtonLifecycle;
    if (lc) lc.start(btn, { label: 'Saving...' });
    return promise
        .then((result) => {
            if (lc) lc.success(btn, { label: 'Saved', message: success });
            return result;
        })
        .catch((e) => {
            if (lc) lc.error(btn, { label: 'Try again', message: e?.payload?.message || error });
            throw e;
        });
}

function openFolderModal(folder = null) {
    $id('bbnp-folder-id').value = folder ? folder.id : '';
    $id('bbnp-folder-name').value = folder ? folder.name : '';
    document.querySelector('#bbnp-folder-modal .modal-custom-title').textContent = folder ? 'Rename folder' : 'New folder';
    $id('bbnp-folder-delete').classList.toggle('d-none', !folder || !state.caps.full_access);
    window.$('#bbnp-folder-modal').modal('show');
    setTimeout(() => $id('bbnp-folder-name').focus(), 300);
}

function openNoteModal() {
    $id('bbnp-note-name').value = '';
    const select = $id('bbnp-note-folder');
    select.textContent = '';
    for (const folder of state.folders) {
        const opt = el('option', '', folder.name);
        opt.value = folder.id;
        if (state.filter.type === 'folder' && state.filter.folderId === folder.id) opt.selected = true;
        select.appendChild(opt);
    }
    window.$('#bbnp-note-modal').modal('show');
    setTimeout(() => $id('bbnp-note-name').focus(), 300);
}

function openShareModal() {
    const d = state.detail;
    if (!d?.share) return;
    const list = $id('bbnp-share-list');
    list.textContent = '';
    $id('bbnp-share-everyone').value = '';
    for (const worker of d.share.workers) {
        const row = el('div', 'bbnp-share-row');
        const avatar = el('span', 'bbnp-share-avatar', worker.name.split(' ').map(p => p[0] || '').join('').slice(0, 2).toUpperCase());
        row.appendChild(avatar);
        row.appendChild(el('span', 'bbnp-share-name', worker.name));
        const select = el('select', 'custom-select bbnp-role-select');
        select.dataset.workerId = worker.worker_id;
        for (const [value, label] of [['', 'No access'], ['viewer', 'Can view'], ['editor', 'Can edit']]) {
            const opt = el('option', '', label);
            opt.value = value;
            if ((worker.role || '') === value) opt.selected = true;
            select.appendChild(opt);
        }
        row.appendChild(select);
        list.appendChild(row);
    }
    if (!d.share.workers.length) {
        list.appendChild(el('div', 'bbnp-share-empty', 'No employees to share with yet.'));
    }
    window.$('#bbnp-share-modal').modal('show');
}

/* ---------------------------------------------------------------- toolbar */

function execToolbar(cmd) {
    const body = $id('bbnp-body');
    body.focus();
    if (cmd === 'bold' || cmd === 'italic' || cmd === 'underline') {
        document.execCommand(cmd);
    } else if (cmd === 'h3') {
        const block = document.queryCommandValue('formatBlock');
        document.execCommand('formatBlock', false, /h3/i.test(block) ? 'p' : 'h3');
    } else if (cmd === 'ul') {
        document.execCommand('insertUnorderedList');
    } else if (cmd === 'checklist') {
        document.execCommand('insertHTML', false,
            '<ul data-checklist="1"><li data-checked="false">To-do item</li></ul>');
    } else if (cmd === 'link') {
        const url = prompt('Link address (https://…):', 'https://');
        if (url && /^https?:\/\//i.test(url)) document.execCommand('createLink', false, url);
    } else if (cmd === 'mark') {
        const sel = window.getSelection();
        const text = sel ? sel.toString() : '';
        if (text) {
            const span = document.createElement('mark');
            span.textContent = text;
            document.execCommand('insertHTML', false, span.outerHTML);
        }
    }
    queueSave();
}

/* ---------------------------------------------------------------- init */

export default {
    init() {
        const root = document.getElementById('bb-notepad');
        if (!root) return;

        applyPayload(window.BBNotepadPayload || {});

        // rail static filters
        root.querySelectorAll('.bbnp-rail__item[data-filter]').forEach(item => {
            item.addEventListener('click', () => {
                state.filter = { type: item.dataset.filter, folderId: null };
                renderRail();
                fetchList();
            });
        });

        // search + sort
        let searchTimer = null;
        $id('bbnp-search').addEventListener('input', (ev) => {
            clearTimeout(searchTimer);
            searchTimer = setTimeout(() => {
                state.search = ev.target.value.trim();
                fetchList();   // server-side: searches every note, not just loaded ones
            }, 250);
        });
        $id('bbnp-sort').addEventListener('change', (ev) => {
            state.sort = ev.target.value;
            fetchList();
        });

        // infinite scroll — pull the next page as the list nears its end
        $id('bbnp-cards').addEventListener('scroll', (ev) => {
            const box = ev.target;
            if (!state.hasMore || state.loading) return;
            if (box.scrollTop + box.clientHeight >= box.scrollHeight - 200) {
                fetchList({ append: true });
            }
        });

        // new folder / note buttons (page head + pane footers)
        for (const btnId of ['bbnp-new-folder-btn', 'bbnp-new-folder-btn-2']) {
            $id(btnId)?.addEventListener('click', () => openFolderModal());
        }
        for (const btnId of ['bbnp-new-note-btn', 'bbnp-new-note-btn-2']) {
            $id(btnId)?.addEventListener('click', () => openNoteModal());
        }

        // folder modal
        $id('bbnp-folder-save').addEventListener('click', async (ev) => {
            const name = $id('bbnp-folder-name').value.trim();
            if (!name) return;
            const folderId = $id('bbnp-folder-id').value;
            const request = folderId
                ? api.patch(`/notes/folders/${folderId}`, { name })
                : api.post('/notes/folders', { name });
            try {
                const payload = await lifecycle(ev.currentTarget, request, {
                    success: folderId ? 'Folder renamed.' : 'Folder created.',
                    error: 'Could not save the folder.',
                });
                applyChange(payload);
                setTimeout(() => window.$('#bbnp-folder-modal').modal('hide'), 900);
            } catch (e) { /* surfaced by lifecycle */ }
        });
        $id('bbnp-folder-delete').addEventListener('click', async (ev) => {
            const folderId = $id('bbnp-folder-id').value;
            if (!folderId) return;
            try {
                const payload = await lifecycle(ev.currentTarget, api.delete(`/notes/folders/${folderId}`), {
                    success: 'Folder deleted.',
                    error: 'Could not delete the folder.',
                });
                applyChange(payload);
                setTimeout(() => window.$('#bbnp-folder-modal').modal('hide'), 900);
            } catch (e) { /* message shown; folder with notes returns 422 */ }
        });

        // note modal
        $id('bbnp-note-create').addEventListener('click', async (ev) => {
            const name = $id('bbnp-note-name').value.trim();
            const folderId = $id('bbnp-note-folder').value;
            if (!name || !folderId) return;
            try {
                const payload = await lifecycle(ev.currentTarget,
                    api.post('/notes/note', { name, folder_id: Number(folderId) }), {
                        success: 'Note created.',
                        error: 'Could not create the note.',
                    });
                applyChange(payload);
                setTimeout(() => {
                    window.$('#bbnp-note-modal').modal('hide');
                    if (payload.note_id) openNote(payload.note_id);
                }, 700);
            } catch (e) { /* surfaced by lifecycle */ }
        });

        // overflow menu (keeps the destructive action away from the icon row)
        const moreBtn = $id('bbnp-more-btn');
        const moreMenu = $id('bbnp-more-menu');
        moreBtn.addEventListener('click', (ev) => {
            ev.stopPropagation();
            const open = moreMenu.classList.toggle('d-none');
            moreBtn.setAttribute('aria-expanded', String(!open));
        });
        document.addEventListener('click', () => {
            moreMenu.classList.add('d-none');
            moreBtn.setAttribute('aria-expanded', 'false');
        });
        document.addEventListener('keydown', (ev) => {
            if (ev.key === 'Escape') moreMenu.classList.add('d-none');
        });

        // delete note
        $id('bbnp-delete').addEventListener('click', () => {
            if (!state.detail) return;
            moreMenu.classList.add('d-none');
            $id('bbnp-delete-name').textContent = state.detail.name;
            window.$('#bbnp-delete-modal').modal('show');
        });
        $id('bbnp-delete-confirm').addEventListener('click', async (ev) => {
            if (!state.openId) return;
            try {
                const payload = await lifecycle(ev.currentTarget, api.delete(`/notes/note/${state.openId}`), {
                    success: 'Note deleted.',
                    error: 'Could not delete the note.',
                });
                applyChange(payload);
                setTimeout(() => {
                    window.$('#bbnp-delete-modal').modal('hide');
                    closeEditor();
                }, 700);
            } catch (e) { /* surfaced by lifecycle */ }
        });

        // pin / move / back
        $id('bbnp-pin').addEventListener('click', async () => {
            if (!state.openId) return;
            const payload = await api.post(`/notes/note/${state.openId}/pin`);
            state.detail.pinned = payload.pinned;
            $id('bbnp-pin').classList.toggle('is-on', payload.pinned);
            applyChange(payload);
        });
        $id('bbnp-crumb').addEventListener('change', (ev) => {
            if (state.openId) moveNote(state.openId, Number(ev.target.value));
        });
        $id('bbnp-back').addEventListener('click', async () => {
            await flushSave();
            closeEditor();
        });

        // autosave
        $id('bbnp-title').addEventListener('input', queueSave);
        $id('bbnp-body').addEventListener('input', queueSave);
        $id('bbnp-title').addEventListener('keydown', (ev) => {
            if (ev.key === 'Enter') { ev.preventDefault(); $id('bbnp-body').focus(); }
        });
        window.addEventListener('beforeunload', () => {
            clearTimeout(state.saveTimer);
            if (state.dirty && state.openId) saveNow();
        });

        // checklist toggle (click on the marker zone of a checklist row)
        $id('bbnp-body').addEventListener('click', (ev) => {
            const li = ev.target.closest('li');
            if (!li || !li.closest('ul[data-checklist]')) return;
            const rect = li.getBoundingClientRect();
            if (ev.clientX - rect.left <= 26) {
                li.dataset.checked = li.dataset.checked === 'true' ? 'false' : 'true';
                queueSave();
            }
        });

        // toolbar
        $id('bbnp-toolbar').addEventListener('click', (ev) => {
            const btn = ev.target.closest('.bbnp-tb');
            if (btn) { ev.preventDefault(); execToolbar(btn.dataset.cmd); }
        });

        // share
        $id('bbnp-share-btn').addEventListener('click', openShareModal);
        $id('bbnp-share-apply').addEventListener('click', async (ev) => {
            if (!state.openId) return;
            const everyone = $id('bbnp-share-everyone').value || null;
            const entries = [...document.querySelectorAll('#bbnp-share-list .bbnp-role-select')]
                .filter(sel => sel.value)
                .map(sel => ({ worker_id: Number(sel.dataset.workerId), role: sel.value }));
            try {
                const detail = await lifecycle(ev.currentTarget,
                    api.post(`/notes/note/${state.openId}/share`, { everyone, entries }), {
                        success: 'Access updated.',
                        error: 'Could not update access.',
                    });
                state.detail = detail;
                // The response carries the new access list — paint it now.
                // Without this the avatars only appeared after a page reload.
                renderSharedWith();
                await refreshData(false);
                setTimeout(() => window.$('#bbnp-share-modal').modal('hide'), 900);
            } catch (e) { /* surfaced by lifecycle */ }
        });
        $id('bbnp-share-everyone').addEventListener('change', (ev) => {
            const role = ev.target.value;
            if (!role) return;
            document.querySelectorAll('#bbnp-share-list .bbnp-role-select').forEach(sel => { sel.value = role; });
        });

        // record links
        $id('bbnp-link-add').addEventListener('click', () => {
            $id('bbnp-linkpick').classList.remove('d-none');
            $id('bbnp-linkpick-input').focus();
        });
        let linkTimer = null;
        $id('bbnp-linkpick-input').addEventListener('input', (ev) => {
            clearTimeout(linkTimer);
            const q = ev.target.value.trim();
            linkTimer = setTimeout(async () => {
                const wrap = $id('bbnp-linkpick-results');
                wrap.textContent = '';
                if (q.length < 2) return;
                const { results } = await api.get(`/notes/link-search?q=${encodeURIComponent(q)}`);
                for (const result of results) {
                    const row = el('div', 'bbnp-linkpick__row', `${result.type === 'customer' ? 'Customer' : 'Job'} · ${result.label}`);
                    row.addEventListener('click', async () => {
                        const detail = await api.post(`/notes/note/${state.openId}/links`, { type: result.type, id: result.id });
                        state.detail = detail;
                        renderLinks();
                        $id('bbnp-linkpick').classList.add('d-none');
                        $id('bbnp-linkpick-input').value = '';
                        wrap.textContent = '';
                    });
                    wrap.appendChild(row);
                }
                if (!results.length) wrap.appendChild(el('div', 'bbnp-linkpick__row is-muted', 'No matches'));
            }, 250);
        });
        document.addEventListener('click', (ev) => {
            if (!ev.target.closest('#bbnp-linkpick') && !ev.target.closest('#bbnp-link-add')) {
                $id('bbnp-linkpick')?.classList.add('d-none');
            }
        });
    },
};
